import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useScrollLock from '../../../hooks/useScrollLock';
import { categoryPath } from '../../../services/storefront';
import {
  IconArrowRight,
  IconArrowUpRight,
  IconBag,
  IconChevronDown,
  IconClose,
  IconMail,
  IconPhone,
  IconSearch,
} from '../../storefront/icons';
import { STORE } from '../../storefront/storeInfo';

// Approval state only — never name the customer's pricing tier.
const TRADE_LABELS = {
  active: 'Trade account approved',
  pending: 'Trade approval pending',
};

// Slide-in navigation for tablet and phone widths: collections as an
// accordion, company links, account actions and contact details.
const MobileMenu = ({
  open,
  onClose,
  logo,
  roots,
  itemCount,
  isAuthenticated,
  isAdmin,
  profile,
  initials,
  tradeStatus,
  guestPitch,
  onSearch,
  onCart,
  onSignIn,
  onApply,
  onSignOut,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const closeRef = useRef(null);

  useScrollLock(open);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <div className={`tp-drawer${open ? ' is-open' : ''}`} inert={!open}>
      <div className="tp-drawer__scrim" onClick={onClose} aria-hidden="true" />
      <aside className="tp-drawer__panel" role="dialog" aria-modal="true" aria-label="Site menu">
        <div className="tp-drawer__head">
          <Link to="/" className="tp-drawer__brand" onClick={onClose}>
            <img src={logo} alt="" className="tp-drawer__logo" />
            <span>BS Trading Co.</span>
          </Link>
          <button ref={closeRef} type="button" className="tp-drawer__close" onClick={onClose} aria-label="Close menu">
            <IconClose size={20} />
          </button>
        </div>

        <div className="tp-drawer__body">
          <button type="button" className="tp-drawer__search" onClick={onSearch}>
            <IconSearch size={18} />
            Search the catalog
          </button>

          <p className="tp-drawer__label">Shop by collection</p>
          <ul className="tp-drawer__roots">
            {roots.map((root) => {
              const expanded = expandedId === root.id;
              return (
                <li key={root.id} className={`tp-drawer__root${expanded ? ' is-expanded' : ''}`}>
                  <div className="tp-drawer__row">
                    <Link to={categoryPath(root.id)} className="tp-drawer__root-link">
                      {root.name}
                    </Link>
                    {root.children.length > 0 && (
                      <button
                        type="button"
                        className="tp-drawer__toggle"
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Hide' : 'Show'} ${root.name} categories`}
                        onClick={() => setExpandedId(expanded ? null : root.id)}
                      >
                        <span>{root.children.length}</span>
                        <IconChevronDown size={16} />
                      </button>
                    )}
                  </div>
                  {root.children.length > 0 && (
                    <div className="tp-drawer__children" inert={!expanded}>
                      <ul>
                        {root.children.map((child) => (
                          <li key={child.id}>
                            <Link to={categoryPath(child.id)}>{child.name}</Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <Link to="/products" className="tp-textlink tp-drawer__all">
            View all products <IconArrowRight size={16} />
          </Link>

          <p className="tp-drawer__label">Company</p>
          <ul className="tp-drawer__links">
            <li><Link to="/wholesale">Trade program</Link></li>
            <li><Link to="/about">About us</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li>
              <a href={STORE.faireUrl} target="_blank" rel="noopener noreferrer">
                Shop us on Faire <IconArrowUpRight size={15} />
              </a>
            </li>
          </ul>

          <div className="tp-drawer__account">
            {isAuthenticated ? (
              <>
                <div className="tp-drawer__who">
                  <span className="tp-avatar tp-avatar--lg">{initials}</span>
                  <span>
                    <span className="tp-drawer__who-name">{profile?.name || 'Your account'}</span>
                    {TRADE_LABELS[tradeStatus.state] && (
                      <span className={`tp-status tp-status--${tradeStatus.state}`}>
                        {TRADE_LABELS[tradeStatus.state]}
                      </span>
                    )}
                  </span>
                </div>
                <div className="tp-drawer__account-actions">
                  <Link to="/dashboard" className="tp-btn tp-btn--small">Orders &amp; account</Link>
                  {isAdmin && <Link to="/admin" className="tp-btn tp-btn--small tp-btn--ghost">Admin panel</Link>}
                  <button type="button" className="tp-btn tp-btn--small tp-btn--ghost" onClick={onSignOut}>Sign out</button>
                </div>
              </>
            ) : (
              <>
                <p className="tp-drawer__pitch">{guestPitch}</p>
                <div className="tp-drawer__account-actions">
                  <button type="button" className="tp-btn tp-btn--small" onClick={onApply}>Apply for trade</button>
                  <button type="button" className="tp-btn tp-btn--small tp-btn--ghost" onClick={onSignIn}>Sign in</button>
                </div>
              </>
            )}
            <button type="button" className="tp-drawer__cart" onClick={onCart}>
              <IconBag size={18} />
              Cart
              <span className="tp-drawer__cart-count">{itemCount}</span>
            </button>
          </div>
        </div>

        <div className="tp-drawer__foot">
          <a href={STORE.tollFree.href}><IconPhone size={15} /> {STORE.tollFree.label}</a>
          <a href={STORE.email.href}><IconMail size={15} /> {STORE.email.label}</a>
          <span className="tp-drawer__hours">{STORE.hours}</span>
        </div>
      </aside>
    </div>
  );
};

export default MobileMenu;
